import { Box, Stack, Typography, styled } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import ExitWarningIcon from 'images/icons/exit-warning-icon.svg?react';
import { Button } from '../Button';
import { PrimaryDialog } from './PrimaryDialog';

const Content = styled(DialogContent)(({ theme }) => ({
    '&.MuiDialogContent-root': {
        padding: theme.spacing(5),
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative', // Allow absolute positioning of close icon
    },
}));

export const Message = styled(Typography)({
    fontSize: '0.875rem',
    lineHeight: '19px',
    fontWeight: 500,
    textAlign: 'center',
    fontFamily: '"General Sans", sans-serif',
});

type ConfirmationDialogProps = {
    open: boolean;
    icon?: React.ReactNode;
    hideIcon?: boolean;
    title: React.ReactNode;
    message: React.ReactNode;
    actionButtonText?: string;
    actionButtonLoading?: boolean;
    isActionButtonDisabled?: boolean;
    cancelButtonText?: string;
    isCancelButtonDisabled?: boolean;
    backButtonText?: string;
    isBackButtonDisabled?: boolean;
    onBack?: () => void;
    onConfirm?: () => void;
    onCancel: () => void;
    onBackdropClick?: () => void;
    closeIcon?: React.ReactNode;
    /** When provided (including `null`), the secondary action is not shown in the main row.
     *  Pass a string to render it as a full-width footer button; pass `null` to hide that
     *  footer without falling back to the main-row outline button (e.g. while loading). */
    footerButtonText?: string | null;
};

export function ConfirmationDialog({
    open,
    hideIcon = false,
    icon = <ExitWarningIcon />,
    title,
    message,
    cancelButtonText = 'Cancel',
    actionButtonText = 'Confirm',
    actionButtonLoading = false,
    isActionButtonDisabled = false,
    isCancelButtonDisabled = false,
    backButtonText = 'Previous',
    isBackButtonDisabled = false,
    onBack,
    onConfirm,
    onCancel,
    onBackdropClick,
    closeIcon,
    footerButtonText,
}: ConfirmationDialogProps) {
    const usesFooterSecondaryAction = footerButtonText !== undefined;
    const showFooterButton = typeof footerButtonText === 'string' && footerButtonText.length > 0;
    const showMainRowOutlineCancel = !usesFooterSecondaryAction;

    return (
        <PrimaryDialog width="586px" height="auto" open={open} onClose={onBackdropClick || onCancel}>
            <Content>
                {closeIcon && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            zIndex: 1,
                        }}
                    >
                        {closeIcon}
                    </Box>
                )}
                {!hideIcon && icon}
                <Stack gap={1}>
                    <Typography variant="h5" fontWeight={700} textAlign="center" lineHeight="normal">
                        {title}
                    </Typography>
                    {message ? typeof message === 'string' ? <Message>{message}</Message> : <Box>{message}</Box> : null}
                </Stack>
                <Stack width="100%" gap={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} width="100%" gap={2}>
                        {onBack && (
                            <Button fullWidth variant="outline" onClick={onBack} disabled={isBackButtonDisabled}>
                                {backButtonText}
                            </Button>
                        )}
                        {showMainRowOutlineCancel && (
                            <Button fullWidth variant="outline" onClick={onCancel} disabled={isCancelButtonDisabled}>
                                {cancelButtonText}
                            </Button>
                        )}
                        <Button
                            fullWidth
                            autoFocus
                            onClick={onConfirm ? onConfirm : onCancel}
                            loading={actionButtonLoading}
                            disabled={isActionButtonDisabled}
                        >
                            {actionButtonText}
                        </Button>
                    </Stack>
                    {showFooterButton && (
                        <Button fullWidth variant="outline" onClick={onCancel} disabled={isCancelButtonDisabled}>
                            {footerButtonText}
                        </Button>
                    )}
                </Stack>
            </Content>
        </PrimaryDialog>
    );
}
